import { MIN_ANIMATION_WINDOW, METRICS_UPDATE_INTERVAL } from '../constants'

/** How long a 1 MB burst should remain visible (ms) */
const TARGET_DURATION = 50_000

export function calculateExpireTime(
  bytes: GraphLink['rxBytes'] | GraphLink['txBytes'],
  lastActive: Session['lastActive'],
  terminatedAt: Session['terminatedAt'] = null,
  currentTime = Date.now()
): number | undefined {
  // If session is terminated, return current time to indicate immediate expiry
  if (terminatedAt) return currentTime

  // Nothing to expire
  if (bytes === undefined) return undefined

  // If session is actively reporting (within last minute), don't expire
  if (lastActive) {
    const lastActiveTime = new Date(lastActive).getTime()
    if (currentTime - lastActiveTime <= METRICS_UPDATE_INTERVAL) return undefined
  }

  // Calculate lifespan for this byte count (linear 500 ms → 50 s)
  const scale = bytes / (1024 * 1024) // Scale relative to 1MB for consistent animation
  const lifespan = MIN_ANIMATION_WINDOW + (TARGET_DURATION - MIN_ANIMATION_WINDOW) * scale

  // Calculate age of this reading
  const startTime = lastActive ? new Date(lastActive).getTime() : currentTime
  const age = currentTime - startTime

  // Already too old? → return current time so caller zeroes the metric
  if (age >= lifespan) return currentTime

  // Otherwise expire in the remaining time, counted from current time
  return currentTime + Math.round(lifespan - age)
}

export function addSessionExpiry(session: Session): Session {
  const currentTime = Date.now()
  const rxExpireTime = calculateExpireTime(session.rxBytes, session.lastActive, session.terminatedAt, currentTime)
  const txExpireTime = calculateExpireTime(session.txBytes, session.lastActive, session.terminatedAt, currentTime)

  return {
    ...session,
    rxBytes: rxExpireTime && rxExpireTime <= currentTime ? 0 : session.rxBytes,
    txBytes: txExpireTime && txExpireTime <= currentTime ? 0 : session.txBytes,
    rxExpireTime,
    txExpireTime,
  }
}

export default addSessionExpiry
